import React, { useState, useEffect } from 'react';
import { BarChart3, DollarSign, Users, TrendingUp, RefreshCw, ShoppingBag } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../services/api';

const PIE_COLORS = [
  '#6366f1', '#ec4899', '#f59e0b', '#10b981', '#3b82f6',
  '#ef4444', '#8b5cf6', '#14b8a6', '#f97316', '#84cc16'
];

function StatCard({ icon: Icon, label, value, color }) {
  return (
    <div className="bg-white rounded-lg shadow p-5 flex items-center">
      <div className={`flex items-center justify-center w-12 h-12 rounded-lg ${color}`}>
        <Icon className="w-6 h-6 text-white" />
      </div>
      <div className="mr-4">
        <p className="text-sm text-gray-500">{label}</p>
        <p className="text-2xl font-bold text-gray-900">{value}</p>
      </div>
    </div>
  );
}

function PieChart({ data }) {
  const entries = Object.entries(data || {}).sort((a, b) => b[1] - a[1]);
  const total = entries.reduce((s, [, v]) => s + v, 0);

  if (!total) {
    return <p className="text-gray-500 text-center py-8">אין נתוני קטגוריות להצגה</p>;
  }

  const radius = 90;
  const cx = 100;
  const cy = 100;
  let cumulative = 0;

  const slices = entries.map(([name, value], i) => {
    const startAngle = (cumulative / total) * 2 * Math.PI;
    cumulative += value;
    const endAngle = (cumulative / total) * 2 * Math.PI;
    const x1 = cx + radius * Math.sin(startAngle);
    const y1 = cy - radius * Math.cos(startAngle);
    const x2 = cx + radius * Math.sin(endAngle);
    const y2 = cy - radius * Math.cos(endAngle);
    const largeArc = endAngle - startAngle > Math.PI ? 1 : 0;
    const path = `M ${cx} ${cy} L ${x1} ${y1} A ${radius} ${radius} 0 ${largeArc} 1 ${x2} ${y2} Z`;
    return { name, value, path, color: PIE_COLORS[i % PIE_COLORS.length] };
  });

  return (
    <div className="flex flex-col sm:flex-row items-center gap-6">
      <svg viewBox="0 0 200 200" className="w-48 h-48 flex-shrink-0">
        {slices.map((s, i) => (
          <path key={i} d={s.path} fill={s.color} stroke="#fff" strokeWidth="1" />
        ))}
      </svg>
      <div className="flex-1 w-full space-y-2">
        {slices.map((s, i) => (
          <div key={i} className="flex items-center justify-between text-sm">
            <div className="flex items-center">
              <span className="inline-block w-3 h-3 rounded-sm ml-2" style={{ backgroundColor: s.color }}></span>
              <span className="text-gray-700">{s.name}</span>
            </div>
            <span className="font-medium text-gray-900">
              {s.value} ({Math.round((s.value / total) * 100)}%)
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function AnalyticsTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadAnalytics(false);
  }, []);

  const loadAnalytics = async (refresh) => {
    if (refresh) setRefreshing(true); else setLoading(true);
    setError(null);
    try {
      const response = await api.get(`/aliexpress/analytics${refresh ? '?refresh=1' : ''}`);
      setData(response.data);
      if (response.data.warning) {
        toast.error('הוצגו נתונים מהמטמון: ' + response.data.warning);
      } else if (refresh) {
        toast.success('הנתונים עודכנו');
      }
    } catch (err) {
      const msg = err.response?.data?.error || 'שגיאה בטעינת אנליטיקה';
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-16">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-600 mx-auto"></div>
        <p className="mt-4 text-gray-600">טוען אנליטיקה...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center flex-wrap gap-2">
        <div className="flex items-center">
          <BarChart3 className="w-8 h-8 text-indigo-600 ml-3" />
          <div>
            <h2 className="text-2xl font-bold text-gray-900">אנליטיקה - AliExpress</h2>
            <p className="text-gray-600 text-sm">עמלות, לקוחות ומוצרים מובילים (3 חודשים אחרונים)</p>
          </div>
        </div>
        <button
          onClick={() => loadAnalytics(true)}
          disabled={refreshing}
          className="flex items-center px-4 py-2 rounded-lg font-medium bg-indigo-100 text-indigo-700 hover:bg-indigo-200 disabled:opacity-50 transition-colors"
        >
          <RefreshCw className={`w-5 h-5 ml-2 ${refreshing ? 'animate-spin' : ''}`} />
          רענן
        </button>
      </div>

      {error && !data && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">{error}</div>
      )}

      {data && (
        <>
          {data.cached && (
            <p className="text-xs text-gray-400">
              נתונים מהמטמון{data.stale ? ' (ייתכן שאינם מעודכנים)' : ''} · עודכן: {new Date(data.fetchedAt).toLocaleString('he-IL')}
            </p>
          )}

          {/* Stat cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard icon={DollarSign} label="סך עמלות" value={`$${data.totalCommissions}`} color="bg-green-500" />
            <StatCard icon={Users} label="לקוחות (משוער)" value={data.uniqueCustomers} color="bg-blue-500" />
            <StatCard icon={TrendingUp} label="עמלה ממוצעת ללקוח" value={`$${data.averageCommission}`} color="bg-purple-500" />
            <StatCard icon={ShoppingBag} label="סך הזמנות" value={data.ordersCount} color="bg-orange-500" />
          </div>

          {/* Monthly goal progress bar */}
          {data.monthlyGoal != null && data.monthlyGoal > 0 && (
            <div className="bg-white rounded-lg shadow p-4 sm:p-6">
              <div className="flex justify-between items-center mb-3">
                <h3 className="font-semibold text-gray-900">המטרה החודשית</h3>
                <span className="text-sm text-gray-500">
                  {new Date().toLocaleDateString('he-IL', { month: 'long', year: 'numeric' })}
                </span>
              </div>
              <div className="flex items-baseline gap-2 mb-3">
                <span className="text-3xl font-bold text-indigo-600">${(data.currentMonthTotal || 0).toFixed(2)}</span>
                <span className="text-gray-400">/ ${data.monthlyGoal.toFixed(2)}</span>
                <span className="text-sm font-medium mr-auto" style={{
                  color: (data.currentMonthTotal || 0) >= data.monthlyGoal ? '#10b981' : '#6366f1'
                }}>
                  {Math.min(Math.round(((data.currentMonthTotal || 0) / data.monthlyGoal) * 100), 100)}%
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-4 overflow-hidden">
                <div
                  className="h-4 rounded-full transition-all duration-1000 ease-out"
                  style={{
                    width: `${Math.min(((data.currentMonthTotal || 0) / data.monthlyGoal) * 100, 100)}%`,
                    background: (data.currentMonthTotal || 0) >= data.monthlyGoal
                      ? 'linear-gradient(90deg, #10b981, #34d399)'
                      : 'linear-gradient(90deg, #6366f1, #818cf8)'
                  }}
                />
              </div>
              {(data.currentMonthTotal || 0) >= data.monthlyGoal && (
                <p className="text-green-600 text-sm mt-2 font-medium">🎉 היעד הושג!</p>
              )}
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* All products */}
            <div className="bg-white rounded-lg shadow p-4 sm:p-6">
              <h3 className="font-semibold text-gray-900 mb-4">כל המוצרים</h3>
              {(data.allProducts || data.topProducts)?.length ? (
                <div className="space-y-3">
                  {(data.allProducts || data.topProducts).map((p, i) => (
                    <a
                      key={i}
                      href={p.productUrl || '#'}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center p-2 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      <span className="flex items-center justify-center w-6 h-6 rounded-full bg-indigo-100 text-indigo-700 text-sm font-bold flex-shrink-0">
                        {i + 1}
                      </span>
                      {p.imageUrl ? (
                        <img src={p.imageUrl} alt="" className="w-12 h-12 rounded object-cover mr-3 flex-shrink-0" />
                      ) : (
                        <div className="w-12 h-12 rounded bg-gray-100 mr-3 flex-shrink-0" />
                      )}
                      <div className="flex-1 min-w-0 mr-3">
                        <p className="text-sm text-gray-800 truncate">{p.title}</p>
                        <div className="flex items-center gap-2">
                          <p className="text-xs text-green-600 font-medium">${p.totalCommission.toFixed(2)}</p>
                          {p.date && (
                            <p className="text-xs text-gray-400">
                              {new Date(p.date).toLocaleString('he-IL', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                            </p>
                          )}
                        </div>
                      </div>
                    </a>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-center py-8">אין מוצרים להצגה</p>
              )}
            </div>

            {/* Category pie chart */}
            <div className="bg-white rounded-lg shadow p-4 sm:p-6">
              <h3 className="font-semibold text-gray-900 mb-4">הזמנות לפי קטגוריה</h3>
              <PieChart data={data.categoryCounts} />
            </div>
          </div>
        </>
      )}
    </div>
  );
}
